import { EntityType, Locale } from '@frontend/common';
import { ProductCategoryClient } from '@frontend/group/api';
import { NodeType } from '@frontend/group/types';
import { CommonMessage } from '@frontend/lang';
import { defaultMapWithAttributes } from '@frontend/product/badge';
import { IProduct, ProductCurrency, ProductType } from '@frontend/product/types';
import { DocType, IMAGE_MIME_TYPES, useBlobRepository, useDocumentRepository } from '@frontend/repository';

export class Product implements IProduct {
    sku: string | null;
    ean: string | null;
    name: string;
    name_nl_be: string | null;
    name_en_us: string | null;
    name_fr_fr: string | null;
    name_de_de: string | null;
    description: string | null;
    description_nl_be: string | null;
    description_en_us: string | null;
    description_fr_fr: string | null;
    description_de_de: string | null;
    user_info: string | null;
    user_info_de_de: string | null;
    user_info_en_us: string | null;
    user_info_fr_fr: string | null;
    user_info_nl_be: string | null;
    currency: ProductCurrency;
    price: number | null;
    consume_questionnaire_id: string | null;
    return_questionnaire_id: string | null;
    kit_id?: string | null;
    attributes: { [key: string]: string } | null;
    update_timestamp: string;
    type: ProductType;
    id: string;
    account_id: string;
    creation_timestamp: string;
    delete_timestamp: string | null;

    constructor(product: IProduct) {
        this.sku = product.sku;
        this.ean = product.ean;
        this.name = product.name;
        this.name_nl_be = product.name_nl_be;
        this.name_en_us = product.name_en_us;
        this.name_fr_fr = product.name_fr_fr;
        this.name_de_de = product.name_de_de;
        this.description = product.description;
        this.description_nl_be = product.description_nl_be;
        this.description_en_us = product.description_en_us;
        this.description_fr_fr = product.description_fr_fr;
        this.description_de_de = product.description_de_de;
        this.consume_questionnaire_id = product.consume_questionnaire_id;
        this.return_questionnaire_id = product.return_questionnaire_id;
        this.kit_id = product.kit_id;
        this.attributes = product.attributes;
        this.update_timestamp = product.update_timestamp;
        this.type = product.type;
        this.id = product.id;
        this.account_id = product.account_id;
        this.creation_timestamp = product.creation_timestamp;
        this.user_info = product.user_info;
        this.user_info_de_de = product.user_info_de_de;
        this.user_info_en_us = product.user_info_en_us;
        this.user_info_fr_fr = product.user_info_fr_fr;
        this.user_info_nl_be = product.user_info_nl_be;
        this.currency = product.currency;
        this.price = product.price;
        this.delete_timestamp = product.delete_timestamp;
    }

    async getImage(documentRepository: ReturnType<typeof useDocumentRepository>): Promise<string | null> {
        let service = await documentRepository.getFiles(EntityType.PRODUCT, this.id);
        const image = service.getDocuments().find((d) => service.getType(d.id) === DocType.IMAGE);
        if (image) return await service.getUrl(image.id);

        //Does not have its own image so check if it has a parent Leaf category with an image
        const members = await ProductCategoryClient.fetchProductCategoryMembers({ entity_id: this.id });
        const groupIds = members.results.map((m) => m.group_id);
        let { results: categories } = await ProductCategoryClient.resolveProductCategories(groupIds);
        categories = categories.filter((c) => c.node_type === NodeType.LEAF);

        let categoryImage: string | null = null;
        while (categoryImage === null && categories.length > 0) {
            const category = categories[0];
            service = await documentRepository.getFiles(EntityType.PRODUCT_CATEGORY, category.id);
            const image = service.getDocuments().find((d) => service.getType(d.id) === DocType.IMAGE);
            if (image) categoryImage = await service.getUrl(image.id);
            categories = categories.slice(1);
        }
        return categoryImage;
    }

    async getBlobImage(blobRepository: ReturnType<typeof useBlobRepository>): Promise<string | null> {
        const ownInfo = await blobRepository.getBlob(this.account_id, EntityType.PRODUCT, this.id);
        if (ownInfo && IMAGE_MIME_TYPES.includes(ownInfo.mimeType)) return ownInfo.url;

        // Fall back to a leaf product-category image
        const members = await ProductCategoryClient.fetchProductCategoryMembers({ entity_id: this.id });
        const groupIds = members.results.map((m) => m.group_id);
        let { results: categories } = await ProductCategoryClient.resolveProductCategories(groupIds);
        categories = categories.filter((c) => c.node_type === NodeType.LEAF);

        for (const category of categories) {
            const info = await blobRepository.getBlob(category.account_id, EntityType.PRODUCT_CATEGORY, category.id);
            if (info && IMAGE_MIME_TYPES.includes(info.mimeType)) return info.url;
        }
        return null;
    }

    getName(locale?: Locale): string | null {
        return defaultMapWithAttributes(this, locale);
    }

    getDefaultName(): string {
        return this.name;
    }

    getTypeDisplayName(): React.ReactNode {
        if (this.type === ProductType.CONSUMABLE) return CommonMessage.OBJECTS.CONSUMABLE.DEFAULT;
        if (this.type === ProductType.EQUIPMENT) return CommonMessage.OBJECTS.REUSABLE.DEFAULT;
        if (this.type === ProductType.KIT) return CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT;
        if (this.type === ProductType.SERVICE) return CommonMessage.OBJECTS.SERVICE.DEFAULT;
        return CommonMessage.OBJECTS.UNKNOWN.DEFAULT;
    }
}
